// src/hooks/useImageLazyLoad.js
import { useState, useEffect, useRef } from 'react';

/**
 * Enhanced hook for optimized image lazy loading with advanced features:
 * - IntersectionObserver for performance
 * - Priority loading support
 * - Connection-aware loading
 * - Error handling and retries
 * 
 * @param {string} src - Original image source
 * @param {string} placeholderSrc - Placeholder to show while loading
 * @param {boolean} priority - Whether this is a high-priority image that should load immediately
 * @param {number} maxRetries - Maximum number of retry attempts for failed images
 * @returns {Object} - Image loading state and ref
 */
const useImageLazyLoad = (src, placeholderSrc, priority = false, maxRetries = 2) => {
  const [imageSrc, setImageSrc] = useState(placeholderSrc || '');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const retryCount = useRef(0);
  const initialLoadRef = useRef(false);
  
  // Reset state when src changes
  useEffect(() => {
    if (src !== imageSrc && src !== null) {
      setImageLoaded(false);
      setError(null);
      initialLoadRef.current = false;
      retryCount.current = 0;
    }
  }, [src, imageSrc]);
  
  // Detect slow connections for adaptive loading
  const isSlowConnection = useRef(false);
  useEffect(() => {
    // Check for slow connections - protect against missing Navigator interface
    try {
      if ('connection' in navigator && navigator.connection) {
        isSlowConnection.current = (
          navigator.connection.saveData ||
          navigator.connection.effectiveType === 'slow-2g' ||
          navigator.connection.effectiveType === '2g' ||
          navigator.connection.effectiveType === '3g'
        );
      }
    } catch (err) {
      console.warn('Could not detect connection type:', err);
    }
  }, []);

  // We'll move these functions inside the useEffect to avoid dependency issues

  // We'll move these functions inside the useEffect to avoid dependency issues
  
  // Set up image load and error events
  useEffect(() => {
    const imgElement = imgRef.current;
    if (!imgElement) return;
    
    // Define these functions inside the useEffect to avoid dependency issues
    // Handle image load success
    const handleImageLoad = () => {
      setImageLoaded(true);
      setError(null);  // Clear any previous errors
    };
    
    // Handle image load failure with retries
    const handleImageError = () => {
      if (retryCount.current < maxRetries) {
        // Implement exponential backoff for retries
        const backoffDelay = Math.pow(2, retryCount.current) * 300;
        
        setTimeout(() => {
          retryCount.current += 1;
          
          // Force reload the image
          if (imgRef.current) {
            const timestamp = new Date().getTime();
            const retrySrc = src.includes('?') 
              ? `${src}&retry=${timestamp}` 
              : `${src}?retry=${timestamp}`;
              
            setImageSrc(retrySrc);
          }
        }, backoffDelay);
      } else {
        // After max retries, show error state
        console.warn(`Failed to load image after ${maxRetries} attempts:`, src);
        setError({
          message: 'Failed to load image after multiple attempts'
        });
        
        // Use placeholder as fallback
        if (placeholderSrc && imageSrc !== placeholderSrc) {
          setImageSrc(placeholderSrc);
        }
      }
    };
    
    // Handle actual image loading process
    const loadImage = () => {
      if (!src || initialLoadRef.current) return;
      initialLoadRef.current = true;
      
      // Validate the source before attempting to load
      if (!src || typeof src !== 'string') {
        setError({ message: 'Invalid image source' });
        return;
      }
      
      // For high-priority images, load immediately without IntersectionObserver
      if (priority) {
        setImageSrc(src);
        return;
      }

      // Otherwise use IntersectionObserver for lazy loading
      try {
        if ('IntersectionObserver' in window && imgRef.current && !observerRef.current) {
          observerRef.current = new IntersectionObserver((entries) => {
            const [entry] = entries;
            
            if (entry.isIntersecting) {
              // Set the real image source when it becomes visible
              setImageSrc(src);
              // Disconnect the observer after loading to save resources
              if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
              }
            }
          }, {
            // Reduced rootMargin to more reasonably detect visibility
            rootMargin: '100px 200px',
            threshold: 0.01
          });
          
          // Start observing the image element if it's available
          if (imgRef.current) {
            observerRef.current.observe(imgRef.current);
          }
        } else {
          // Fallback for browsers that don't support IntersectionObserver
          setImageSrc(src);
        }
      } catch (err) {
        console.warn('Error setting up IntersectionObserver:', err);
        // Fall back to immediate loading
        setImageSrc(src);
      }
    };
    
    // Add event listeners
    imgElement.addEventListener('load', handleImageLoad);
    imgElement.addEventListener('error', handleImageError);
    
    // Start loading process
    loadImage();
    
    // Clean up
    return () => {
      if (imgElement) {
        imgElement.removeEventListener('load', handleImageLoad);
        imgElement.removeEventListener('error', handleImageError);
      }
      
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, placeholderSrc, priority, maxRetries]); // Only depend on stable props
  
  // Clean up observer when component unmounts
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);
  
  return { imageSrc, imageLoaded, imgRef, error, isSlowConnection: isSlowConnection.current };
};

export default useImageLazyLoad;